import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const ITCompaniesDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'it-infrastructure-management',
      title: 'IT Infrastructure Management',
      description: 'Design, deploy, and manage robust IT infrastructure that keeps your Dubai business running smoothly. We handle everything from server management and network architecture to storage solutions and disaster recovery planning.',
      icon: ICONS.serverInfrastructure,
      features: [
        'Server & Network Administration',
        'Cloud Infrastructure Setup & Migration',
        'Backup & Disaster Recovery Planning',
        'Performance Monitoring & Optimization',
        'Hardware Lifecycle Management',
        'Virtualization & Hybrid Cloud Solutions'
      ]
    },
    {
      id: 'digital-transformation',
      title: 'Digital Transformation',
      description: 'Modernize your business operations with end-to-end digital transformation strategies. We help Dubai organizations adopt new technologies, automate workflows, and create digital experiences that drive growth and efficiency.',
      icon: ICONS.process,
      features: [
        'Business Process Automation',
        'Legacy System Modernization',
        'Digital Workplace Solutions',
        'Data Analytics & Business Intelligence',
        'API Integration & Development',
        'Change Management & Training'
      ]
    },
    {
      id: 'cybersecurity-solutions',
      title: 'Cybersecurity Solutions',
      description: 'Protect your business with comprehensive cybersecurity services tailored for the Dubai market. We implement multi-layered security strategies that safeguard your data, applications, and infrastructure from evolving threats.',
      icon: ICONS.security,
      features: [
        'Security Assessment & Penetration Testing',
        'Endpoint Protection & EDR',
        'Network Security & Firewall Management',
        'Identity & Access Management (IAM)',
        'Security Compliance & Auditing',
        'Incident Response & Forensics'
      ]
    },
    {
      id: 'it-strategy-consulting',
      title: 'IT Strategy & Consulting',
      description: 'Align your technology investments with your business objectives through expert IT consulting. We develop roadmaps that help Dubai businesses make informed decisions about technology adoption, vendor selection, and resource allocation.',
      icon: ICONS.consulting,
      features: [
        'IT Roadmap & Strategy Development',
        'Technology Stack Assessment',
        'Vendor Evaluation & Selection',
        'IT Budget Planning & Optimization',
        'Risk Assessment & Mitigation',
        'CTO-as-a-Service'
      ]
    }
  ]

  const tools = [
    { name: 'AWS', category: 'Cloud' },
    { name: 'Azure', category: 'Cloud' },
    { name: 'Microsoft 365', category: 'Productivity' },
    { name: 'Cisco', category: 'Networking' },
    { name: 'VMware', category: 'Virtualization' },
    { name: 'Fortinet', category: 'Security' },
    { name: 'Splunk', category: 'Analytics' },
    { name: 'ServiceNow', category: 'ITSM' },
    { name: 'Jira', category: 'Project Mgmt' },
    { name: 'Confluence', category: 'Collaboration' },
    { name: 'Okta', category: 'Identity' },
    { name: 'Cloudflare', category: 'CDN & Security' }
  ]

  const benefits = [
    {
      title: 'Systems Managed',
      description: 'Enterprise-grade IT systems deployed and managed across Dubai businesses of all sizes.',
      stat: '500+'
    },
    {
      title: 'Network Uptime',
      description: 'Industry-leading reliability through proactive monitoring and redundant infrastructure.',
      stat: '99.99%'
    },
    {
      title: 'Support Available',
      description: 'Round-the-clock technical support ensuring your business never experiences downtime.',
      stat: '24/7'
    },
    {
      title: 'Cost Reduction',
      description: 'Average cost savings achieved through infrastructure optimization and smart automation.',
      stat: '60%'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'What IT services do you offer in Dubai?',
      answer: 'We offer comprehensive IT services in Dubai including IT infrastructure management, cloud migration and setup, cybersecurity solutions, digital transformation consulting, IT strategy and roadmap development, vendor selection, CTO-as-a-service, business process automation, and 24/7 IT support. We serve businesses of all sizes from startups to enterprises across the UAE.'
    },
    {
      question: 'How much do IT services cost in Dubai?',
      answer: 'IT service costs in Dubai vary by scope and engagement model. IT consulting rates range from AED 400 to AED 1,500 per hour. Managed IT support packages start from AED 5,000 per month for small businesses and AED 15,000 to AED 50,000 per month for mid-sized companies. Project-based engagements such as infrastructure setup or cloud migration range from AED 25,000 to AED 200,000 depending on complexity. We offer flexible pricing including fixed-price projects and monthly retainers.'
    },
    {
      question: 'How do I choose the right IT company in Dubai?',
      answer: 'When selecting an IT company in Dubai, consider their experience in your industry, range of services offered, client references and case studies, certifications (AWS, Azure, Cisco, etc.), response times and SLA guarantees, data residency and compliance capabilities, and pricing transparency. Look for a partner who understands UAE business regulations and can scale with your growth rather than just a vendor who provides basic IT support.'
    },
    {
      question: 'Do you provide IT support for businesses in Dubai free zones?',
      answer: 'Yes, we provide IT services to businesses across all Dubai free zones including DIFC, DMCC, Dubai Internet City, Dubai Media City, JAFZA, Dubai Silicon Oasis, and DAFZA. We understand the specific IT infrastructure requirements, data residency policies, and compliance frameworks applicable to different free zone authorities and can tailor our solutions accordingly.'
    },
    {
      question: 'Can you help migrate our business to the cloud?',
      answer: 'Yes, cloud migration is one of our core IT services. We handle the full migration lifecycle including assessment of your current infrastructure, selection of the right cloud platform (AWS, Azure, or GCP), migration planning and execution, data migration, application refactoring if needed, and post-migration optimization. We also provide hybrid cloud solutions for businesses that need to maintain some on-premise infrastructure due to regulatory or performance requirements.'
    },
    {
      question: 'What cybersecurity services do you offer for Dubai businesses?',
      answer: 'Our cybersecurity services include security assessments and penetration testing, endpoint protection and EDR deployment, network security and firewall management, identity and access management (IAM), security compliance auditing for standards like ISO 27001, SOC 2, and NESA, incident response planning and forensics, and security awareness training for employees. We implement multi-layered security strategies tailored to the threat landscape in the UAE.'
    },
    {
      question: 'How quickly can you respond to IT issues?',
      answer: 'Our response times depend on issue severity and your service level agreement. For critical issues affecting business operations, we guarantee a 15-minute response time with 24/7 availability. For high-priority issues, our response time is within 1 hour. Standard requests are addressed within 4 business hours. We provide dedicated account managers and multiple communication channels including phone, email, and ticketing systems.'
    },
    {
      question: 'What industries do you serve with IT services in Dubai?',
      answer: 'We serve a wide range of industries in Dubai and the UAE including finance and banking, healthcare, retail and e-commerce, logistics and supply chain, real estate and construction, hospitality and tourism, education, government and public sector, and technology startups. Our IT solutions are adapted to meet industry-specific compliance requirements and operational needs.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'IT Companies Dubai', path: '/it-companies-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Leading IT Company in Dubai | Technology Solutions & IT Services UAE"
        description="Leading IT company in Dubai providing comprehensive technology solutions. From IT infrastructure management and cybersecurity to digital transformation and IT consulting. Partner with Demirix for reliable IT services in the UAE."
        keywords={[
          'it companies in dubai',
          'it company dubai',
          'it solutions dubai',
          'technology companies dubai',
          'it services dubai',
          'it consulting dubai',
          'it infrastructure dubai',
          'cybersecurity solutions dubai',
          'digital transformation dubai',
          'managed it services uae',
          'it support dubai',
          'enterprise it solutions uae'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              IT Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Leading IT Company in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Comprehensive IT solutions for Dubai businesses, from infrastructure management and
              cybersecurity to digital transformation and strategic IT consulting. We help organizations
              leverage technology to drive growth and operational excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule IT Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our IT Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end IT solutions designed for Dubai businesses looking to optimize
                their technology infrastructure and accelerate digital growth.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Tools & Technologies We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage industry-leading technologies to deliver reliable, scalable IT solutions for your business.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about IT services in Dubai answered by our technology team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Dubai Services
              </Link>
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Transform Your IT Infrastructure?"
        description="Let's discuss how our IT solutions can streamline your operations and help your Dubai business achieve its technology goals with confidence."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default ITCompaniesDubai
